import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Play, Pause, SkipForward, X } from 'lucide-react';

const MiniAudioPlayer = ({ article, audioType, onClose, initialIsPlaying, onPlayingStateChange, darkMode = false }) => {
  const [isPlaying, setIsPlaying] = useState(initialIsPlaying || false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  // Sync with parent state (only update if different)
  useEffect(() => {
    if (initialIsPlaying !== isPlaying) {
      setIsPlaying(initialIsPlaying);
    }
  }, [initialIsPlaying]); // Removed isPlaying from deps to prevent loop

  // Construct the backend audio URL
  const audioEndpoint = article ? (audioType === 'summary' 
    ? `/api/audio/${article.id}/summary`
    : `/api/audio/${article.id}/full`) : null;
  
  // Fetch actual audio URL from backend
  useEffect(() => {
    const fetchAudioUrl = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}${audioEndpoint}`);
        const data = await response.json();
        if (data.url) {
          setAudioUrl(data.url);
        }
      } catch (error) {
        console.error('Error fetching audio URL:', error);
      }
    };
    
    if (article && audioEndpoint) {
      fetchAudioUrl();
    }
  }, [article, audioEndpoint]);

  // Auto-play when audio URL is loaded
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.load();
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.log('Auto-play prevented:', error);
            // Auto-play was prevented, user needs to click play
            setIsPlaying(false);
          });
      }
    }
  }, [audioUrl]);

  useEffect(() => {
    if (article && audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
      
      if (isPlaying) {
        audioRef.current.play().catch(() => {
          // Mock playback simulation when real audio isn't available
          const interval = setInterval(() => {
            setCurrentTime(prev => {
              if (prev >= 180) {
                setIsPlaying(false);
                clearInterval(interval);
                return 180;
              }
              return prev + 1;
            });
          }, 1000 / playbackSpeed);
          return () => clearInterval(interval);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, article, playbackSpeed]);

  const handlePlayPause = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    // Notify parent of the change
    if (onPlayingStateChange) {
      onPlayingStateChange(newPlayingState);
    }
  };

  const handleSkipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 10, duration);
    }
    setCurrentTime(prev => Math.min(prev + 10, duration || 180));
  };

  const toggleSpeed = () => {
    const speeds = [1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!article) return null;

  return (
    <div className={`fixed left-0 right-0 border-t shadow-lg z-50 ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`} style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 8px))' }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setIsPlaying(false)}
      />
      
      <div className="px-4 py-2 flex items-center gap-3">
        {/* Article Thumbnail */}
        <img
          src={article.image}
          alt={article.title}
          className="w-12 h-12 rounded object-cover"
        />

        {/* Article Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-blue-500 font-medium">
            Playing {audioType === 'summary' ? 'Summary' : 'Full Article'}
          </p>
          <p className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {article.title}
          </p>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatTime(currentTime)}</p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePlayPause}
            size="icon"
            className="h-10 w-10 rounded-full bg-green-600 hover:bg-green-700"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" fill="currentColor" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
            )}
          </Button>
          
          <Button
            onClick={handleSkipForward}
            size="icon"
            variant="ghost"
            className={`h-10 w-10 ${darkMode ? 'text-white hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            <SkipForward className="w-5 h-5" />
          </Button>
          
          <Button
            onClick={toggleSpeed}
            variant="ghost"
            className={`h-10 px-3 text-sm font-semibold ${darkMode ? 'text-white hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {playbackSpeed}x
          </Button>

          <Button
            onClick={onClose}
            size="icon"
            variant="ghost"
            className={`h-8 w-8 ${darkMode ? 'text-white hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className={`h-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <div
          className="h-full bg-green-600 transition-all"
          style={{ width: `${(currentTime / (duration || 180)) * 100}%` }}
        />
      </div>
    </div>
  );
};

export default MiniAudioPlayer;